import { useEffect, useState, type ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";
import { AxonMark } from "../../design-system/primitives/AxonMark";
import { Icon } from "../../design-system/primitives/Icon";
import { Modal } from "../../design-system/primitives/Modal";
import { cn } from "../../design-system/tokens";
import { FeedbackButton } from "../feedback/FeedbackButton";
import { APP_VERSION_LABEL } from "../../lib/appVersion";
import {
  I18N_STORAGE_KEY,
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from "../../i18n";

const AXON_GROUP_URL = "https://axongroup.com.co/";
const AXON_GROUP_LOGO_URL = `${import.meta.env.BASE_URL}axon-group-logo.svg`;

/** Flag en localStorage que marca el dialogo de bienvenida como visto. El
 *  valor es un timestamp ISO del primer cierre, no un booleano, para que
 *  futuras migraciones puedan decidir "visto en la version X" sin romper a
 *  los visitantes recurrentes. */
const WELCOME_SEEN_KEY = "axon.tags.welcome.seen";

function hasSeenWelcome(): boolean {
  try {
    return window.localStorage.getItem(WELCOME_SEEN_KEY) !== null;
  } catch {
    // Modo privado / almacenamiento deshabilitado: comportarse como
    // "visto" para no molestar al usuario en cada carga.
    return true;
  }
}

function markWelcomeSeen(): void {
  try {
    window.localStorage.setItem(WELCOME_SEEN_KEY, new Date().toISOString());
  } catch {
    // Ignorar: mejor esfuerzo.
  }
}

const CAPABILITIES = [
  "IEC 61850 / MMS",
  "SIEMENS SIPROTEC 5",
  "ABB / Hitachi",
  "SEL",
  "Excel / CSV",
  "SCL (.scd / .cid)",
];

/**
 * Afordancia "About" centrada en el uso. El dialogo responde tres
 * preguntas: para que sirve la herramienta, que entradas procesa y si los
 * datos salen del dispositivo. Los metadatos de build viven en la linea de
 * version del encabezado.
 *
 * Comportamiento de primera visita: en la primera sesion del operador (sin
 * la clave `axon.tags.welcome.seen` en `localStorage`), el dialogo se abre
 * automaticamente como tarjeta de bienvenida con dos llamados a la accion:
 * "Conoce Axon Group" (abre `axongroup.com.co` en una pestana nueva) y
 * "Comenzar" (descarta). Las visitas siguientes dejan el dialogo cerrado;
 * el operador lo alcanza con el icono `info` de la barra superior.
 *
 * Tambien monta el boton de Feedback ("Comentar") a su izquierda, igual que
 * en el proyecto de referencia, y aloja el selector de idioma (chips ES/EN).
 */
export function AboutButton() {
  const { t } = useTranslation("about");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!hasSeenWelcome()) {
      setOpen(true);
    }
  }, []);

  const handleClose = () => {
    setOpen(false);
    markWelcomeSeen();
  };

  return (
    <>
      <FeedbackButton />
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t("aria.trigger")}
        className="inline-flex h-7 w-7 items-center justify-center rounded text-[var(--color-on-surface-variant)] transition-colors hover:bg-[var(--color-surface-variant)] hover:text-[var(--color-on-surface)]"
      >
        <Icon name="info" size={18} />
      </button>

      <Modal
        open={open}
        onClose={handleClose}
        ariaLabel={t("aria.modal")}
        widthClass="w-[min(460px,92vw)]"
      >
        <div className="flex flex-col gap-4 p-6">
          <div className="flex items-center gap-3 pr-8">
            <AxonMark size={36} />
            <div className="flex flex-col gap-0.5">
              <h2 className="text-lg font-semibold text-[var(--color-on-surface)]">
                {t("title")}
              </h2>
              <span className="font-mono text-[10px] text-[var(--color-on-surface-variant)]">
                {APP_VERSION_LABEL}
              </span>
            </div>
          </div>

          <p className="text-sm leading-relaxed text-[var(--color-on-surface-variant)]">
            <Trans
              i18nKey="description"
              ns="about"
              components={{
                mono: <span className="font-mono text-[var(--color-brand)]" />,
              }}
            />
          </p>

          <Section title={t("sections.capabilities")}>
            <div className="flex flex-wrap gap-1.5">
              {CAPABILITIES.map((label) => (
                <CapabilityChip key={label} label={label} />
              ))}
            </div>
          </Section>

          <Section title={t("sections.privacy")}>
            <p className="text-sm text-[var(--color-on-surface-variant)]">
              {t("privacy")}
            </p>
          </Section>

          <Section title={t("sections.language")}>
            <LanguageSwitcher />
          </Section>

          <Section title={t("sections.about")}>
            <img
              src={AXON_GROUP_LOGO_URL}
              alt="Axon Group"
              className="h-8 w-auto select-none"
              draggable={false}
            />
            <p className="text-sm text-[var(--color-on-surface-variant)]">
              <Trans
                i18nKey="group"
                ns="about"
                components={{
                  link: (
                    <a
                      href={AXON_GROUP_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[var(--color-brand)] hover:underline"
                    />
                  ),
                }}
              />
            </p>
          </Section>

          <div className="flex flex-col gap-2 pt-1">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <a
                href={AXON_GROUP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-1.5 rounded-md border border-[var(--color-brand-border-30)] px-4 py-2 text-sm font-medium text-[var(--color-brand)] transition-colors hover:bg-[var(--color-brand-soft-10)]"
              >
                {t("cta.visit")}
                <Icon name="open_in_new" size={14} />
              </a>
              <button
                type="button"
                onClick={handleClose}
                className="rounded-md bg-[var(--color-brand)] px-4 py-2 text-sm font-medium text-black transition-opacity hover:opacity-90"
              >
                {t("cta.start")}
              </button>
            </div>
            <span className="text-xs text-[var(--color-on-surface-variant)]/70">
              <Trans
                i18nKey="footer"
                ns="about"
                values={{ year: new Date().getFullYear() }}
                components={{
                  link: (
                    <a
                      href={AXON_GROUP_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[var(--color-brand)] hover:underline"
                    />
                  ),
                }}
              />
            </span>
          </div>
        </div>
      </Modal>
    </>
  );
}

/**
 * Selector de idioma — dos chips ES / EN que llaman a
 * `i18n.changeLanguage` y persisten la elección bajo `axon.tags.i18n.lang`.
 * Vive en el diálogo About para no competir con las acciones por listado
 * en la barra superior.
 */
function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const active = (i18n.resolvedLanguage ?? "es") as SupportedLanguage;

  const onPick = (code: SupportedLanguage) => {
    void i18n.changeLanguage(code);
    try {
      window.localStorage.setItem(I18N_STORAGE_KEY, code);
    } catch {
      // Almacenamiento deshabilitado: el cambio de idioma sigue
      // funcionando para esta sesión.
    }
  };

  return (
    <div className="flex gap-1.5">
      {SUPPORTED_LANGUAGES.map((code) => {
        const isActive = code === active;
        return (
          <button
            key={code}
            type="button"
            onClick={() => onPick(code)}
            aria-pressed={isActive}
            className={cn(
              "inline-flex h-7 items-center rounded border px-3 font-mono text-[11px] uppercase tracking-wider transition-colors focus:outline-none",
              isActive
                ? "border-[var(--color-brand-border-60)] bg-[var(--color-brand-soft-10)] text-[var(--color-brand)]"
                : "border-[var(--color-outline-variant)] bg-[var(--color-surface-container-low)] text-[var(--color-on-surface-variant)] hover:border-[var(--color-border)] hover:text-[var(--color-on-surface)]",
            )}
          >
            {code}
          </button>
        );
      })}
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <h3 className="text-[11px] uppercase tracking-wide text-[var(--color-on-surface-variant)]">
        {title}
      </h3>
      {children}
    </div>
  );
}

function CapabilityChip({ label }: { label: string }) {
  return (
    <span className="inline-flex h-6 items-center gap-1.5 rounded border border-[var(--color-outline-variant)] bg-[var(--color-surface-container-low)] px-2 font-mono text-[10px] text-[var(--color-on-surface-variant)]">
      <Icon name="check" size={12} className="text-[var(--color-brand)]/80" />
      <span>{label}</span>
    </span>
  );
}
